import React, { Fragment, useEffect, useState, useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { useMappedState, useDispatch } from 'redux-react-hook';
import { Link } from '@reach/router';
import { linkTo } from 'utils/links';
import { Trans, t } from '@lingui/macro';
import { createBuffered } from 'utils/buffered';
import i18n from 'config/i18n';

import { toggleSearch, search } from 'store/modules/search/actions';
import { getAlbums, getArtists, isSearching, getSongs } from 'store/modules/search/selectors';

import ArtistPanel from 'containers/User/Artist';
import Button from 'components/Button';
import Image from 'components/Image';
import Panel from 'components/Panel';
import TextField from 'components/TextField';
import CloseIcon from 'components/Icon/X';
import SearchIcon from 'components/Icon/Search';
import SongThumbnail from 'containers/Song/Thumbnail';

import empty from './empty.svg';
import './styles.css';

const mapState = state => ({
  albums: getAlbums(state),
  artists: getArtists(state),
  songs: getSongs(state),
  searching: isSearching(state),
});

export default function Search() {
  const { albums, artists, searching, songs } = useMappedState(mapState);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [query, setQuery] = useState('');
  const dispatch = useDispatch();
  const isEmpty = albums.length === 0 && artists.length === 0 && songs.length === 0;
  const onClose = useCallback(() => {
    setVisible(false)
    setTimeout(() => dispatch(toggleSearch()), 500);
  }, []);
  const onSearch = useMemo(() => createBuffered((value) => {
    if (value) {
      setLoading(true);
      dispatch(search(value))
        .then(() => setLoading(false))
        .catch(() => setLoading(false));
    }
  }, 500), []);
  const onChange = (event) => {
    const { value } = event.target;
    setQuery(value);
    onSearch(value);
  }
  useEffect(() => {
    setVisible(searching);
  }, [searching]);

  return (
    <div className={classNames('Search fixed inset-0 z-50 bg-page', {
      'show': visible,
      'hide': !visible,
      'block': searching,
      'hidden': !searching,
    })}>
      <Button type="icon" onClick={onClose} className="absolute hidden md:block" style={{top: 40, right: 50}}>
        <CloseIcon width={40} height={40} />
      </Button>
      <div className="h-screen overflow-auto scrolling-touch">
        <div className="flex justify-center">
          <div className="w-full px-4 pt-8 md:w-2/3 md:pt-16 xl:w-1/3 xl:pt-32">
            <TextField
              value={query}
              onChange={onChange}
              Icon={SearchIcon}
              placeholder={i18n._(t`Search songs, albums, artists and posts...`)}
              className="mb-4"
            />
            <div className="flex justify-center md:hidden">
              <Button className="mr-2"><Trans>Search</Trans></Button> <Button onClick={onClose} type="secondary"><Trans>Cancel</Trans></Button>
            </div>
            {isEmpty &&
              <Image src={empty} className="w-2/3 mx-auto mt-32 opacity-50" />
            }
          </div>
        </div>
        <div className="mt-8 mx-4 md:mx-8 lg:mx-16 xl:mx-32">
          <SongResults songs={songs} onClose={onClose} />
          <AlbumResults albums={albums} onClose={onClose} />
          <ArtistResults artists={artists} onClose={onClose} />
        </div>
      </div>
    </div>
  );
}

function SongResults({ songs, onClose }) {
  if (songs.length > 0) {
    return (
      <Fragment>
        <h2 className="text-default text-default text-lg mb-4 md:px-2 ">{songs.length} <Trans>Songs</Trans></h2>
        <div className="whitespace-no-wrap overflow-auto scrolling-touch">
          {songs.map(song =>
            <div key={song.id} className="inline-block w-32 mb-8 md:px-2 md:w-1/3 lg:w-1/5 xl:w-1/6" onClick={onClose}>
              <SongThumbnail song={song} list={songs} vertical />
            </div>
          )}
        </div>
      </Fragment>
    );
  }

  return null;
}

function AlbumResults({ albums, onClose }) {
  if (albums.length > 0) {
    return (
      <Fragment>
        <h2 className="text-default text-default text-lg mb-4 md:px-2 ">{albums.length} <Trans>Albums</Trans></h2>
        <div className="whitespace-no-wrap overflow-auto scrolling-touch">
          {albums.map(album =>
            <div key={album.id} className="inline-block w-40 mb-8 px-2 md:w-1/3 lg:w-1/5 xl:w-1/6" onClick={onClose}>
              <Link to={linkTo('/albums/:slug', album)}>
                <Image src={album.image} className="rounded" />
              </Link>
              <h4 className="leading-tight mt-1">
                <Link to={linkTo('/albums/:slug', album)} className="block truncate font-sans text-base font-medium text-default paragraph hover:underline xl:text-lg xl:font-medium">
                  {album.title}
                </Link>
              </h4>
              <p className="text-sm text-secondary">
                <Link to={linkTo('/members/profiles/:slug', album.author)} className="hover:underline">{album.author.name}</Link> • {(new Date(album.released)).getFullYear()}
              </p>
            </div>
          )}
        </div>
      </Fragment>
    );
  }

  return null;
}

function ArtistResults({ artists, onClose }) {
  if (artists.length > 0) {
    return (
      <Fragment>
        <h2 className="text-default text-default text-lg mb-4 md:px-2 ">{artists.length} <Trans>Artists</Trans></h2>
        <div className="whitespace-no-wrap overflow-auto scrolling-touch">
          {artists.map(artist =>
            <div key={artist.id} className="inline-block w-40 mb-8 px-2 md:w-1/3 lg:w-1/5 xl:w-1/6" onClick={onClose}>
              <ArtistPanel artist={artist} />
            </div>
          )}
        </div>
      </Fragment>
    );
  }

  return null;
}
